/**
 * Persistenza sessioni partita su IndexedDB.
 * Store: sessions (key=gameType+gameId), stats (key=gameType+entityId).
 */

import { openDB, type IDBPDatabase } from 'idb';
import { isStorageConsentAccepted } from './privacy';
import type { SerializedSession } from './types';

const DB_NAME = 'gamez';
const DB_VERSION = 1;

interface GamezSchema {
  sessions: { key: string; value: SerializedSession };
  stats: {
    key: string;
    value: {
      key: string;
      gameType: string;
      entityId: string;
      wins: number;
      games: number;
      avgAttempts: number;
      bestAttempts: number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase> | null = null;
const memorySessions = new Map<string, SerializedSession>();

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('sessions')) {
          db.createObjectStore('sessions', { keyPath: 'gameId' });
        }
        if (!db.objectStoreNames.contains('stats')) {
          db.createObjectStore('stats', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

function sessionKey(gameType: string, gameId: string): string {
  return `${gameType}::${gameId}`;
}

export async function saveSession(session: SerializedSession): Promise<void> {
  const toStore: SerializedSession = {
    ...session,
    gameId: sessionKey(session.gameType, session.gameId.replace(/^.*::/, '')),
    updatedAt: Date.now(),
  };
  if (!isStorageConsentAccepted()) {
    memorySessions.set(toStore.gameId, toStore);
    return;
  }
  const db = await getDb();
  await db.put('sessions', toStore);
}

export async function loadSession(
  gameType: string,
  gameId: string,
): Promise<SerializedSession | undefined> {
  if (!isStorageConsentAccepted()) {
    return memorySessions.get(sessionKey(gameType, gameId));
  }
  const db = await getDb();
  return db.get('sessions', sessionKey(gameType, gameId)) as Promise<
    SerializedSession | undefined
  >;
}

export async function listActiveSessions(): Promise<SerializedSession[]> {
  if (!isStorageConsentAccepted()) {
    return Array.from(memorySessions.values()).filter((s) => s.status === 'active');
  }
  const db = await getDb();
  const all = (await db.getAll('sessions')) as SerializedSession[];
  return all.filter((s) => s.status === 'active');
}

export async function deleteSession(gameType: string, gameId: string): Promise<void> {
  memorySessions.delete(sessionKey(gameType, gameId));
  if (!isStorageConsentAccepted()) return;
  const db = await getDb();
  await db.delete('sessions', sessionKey(gameType, gameId));
}

export async function clearStoredGameData(): Promise<void> {
  memorySessions.clear();
  const db = dbPromise ? await dbPromise.catch(() => null) : null;
  db?.close();
  dbPromise = null;

  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
}

export async function recordStat(args: {
  gameType: string;
  entityId: string;
  attempts: number;
  won: boolean;
}): Promise<void> {
  if (!isStorageConsentAccepted()) return;
  const db = await getDb();
  const key = `${args.gameType}::${args.entityId}`;
  const tx = db.transaction('stats', 'readwrite');
  const existing = (await tx.store.get(key)) as GamezSchema['stats']['value'] | undefined;
  const next: GamezSchema['stats']['value'] = existing
    ? {
        ...existing,
        games: existing.games + 1,
        wins: existing.wins + (args.won ? 1 : 0),
        avgAttempts:
          (existing.avgAttempts * existing.games + args.attempts) / (existing.games + 1),
        bestAttempts: args.won
          ? Math.min(existing.bestAttempts || args.attempts, args.attempts)
          : existing.bestAttempts,
      }
    : {
        key,
        gameType: args.gameType,
        entityId: args.entityId,
        games: 1,
        wins: args.won ? 1 : 0,
        avgAttempts: args.attempts,
        bestAttempts: args.won ? args.attempts : 0,
      };
  await tx.store.put(next);
  await tx.done;
}
